import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faQuestionCircle, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'What is GlobalPreps?',
      answer:
        'GlobalPreps is a comprehensive platform for certification exam preparation. We provide practice questions, exam simulations, and progress tracking to help you succeed in your certification journey.',
    },
    {
      question: 'How do I get started?',
      answer:
        'Simply create a free account, browse our available certifications, and start practicing with our free questions. You can upgrade to premium for unlimited access and exam mode.',
    },
    {
      question: 'What is the difference between Practice Mode and Exam Mode?',
      answer:
        'Practice Mode provides instant feedback after each question with explanations, perfect for learning. Exam Mode simulates real exam conditions with a timer and no immediate feedback, ideal for testing your readiness.',
    },
    {
      question: 'How much does a subscription cost?',
      answer:
        'We offer flexible subscription plans: 30 days ($9.99), 60 days ($17.99), 90 days ($24.99), and 1 year ($99.99). All plans include unlimited questions and full exam mode access.',
    },
    {
      question: 'Can I cancel my subscription?',
      answer:
        'Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period. We do not offer refunds for partial periods.',
    },
    {
      question: 'Are the questions similar to the actual exam?',
      answer:
        'Our questions are designed to closely mirror the format and difficulty of actual certification exams. However, we cannot guarantee exact similarity as exam content is proprietary.',
    },
    {
      question: 'How do I track my progress?',
      answer:
        'Your dashboard shows your overall performance, recent activity, and progress for each certification. You can view detailed analytics including scores, topics mastered, and areas for improvement.',
    },
    {
      question: 'Is my payment information secure?',
      answer:
        'Yes, we use industry-standard encryption and work with trusted payment processors. We never store your full payment details on our servers.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="gp-page py-6">
      <div className="gp-container max-w-3xl">
        <Link to="/" className="gp-back-link mb-4">
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          Back to Home
        </Link>

        <div className="gp-page-header">
          <div className="flex items-center gap-2 mb-1">
            <span className="gp-icon-tile">
              <FontAwesomeIcon icon={faQuestionCircle} aria-hidden="true" />
            </span>
            <h1 className="gp-page-title">Frequently Asked Questions</h1>
          </div>
          <p className="gp-page-subtitle">Find answers to common questions about GlobalPreps</p>
        </div>

        <div className="space-y-2">
          {faqs.map((faq, index) => (
            <div key={index} className="gp-card p-3.5">
              <button
                type="button"
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between text-left gap-3"
              >
                <h3 className="text-sm font-semibold" style={{ color: 'var(--gp-ink)' }}>
                  {faq.question}
                </h3>
                <FontAwesomeIcon
                  icon={openIndex === index ? faChevronUp : faChevronDown}
                  className="gp-text-muted flex-shrink-0 text-xs"
                  aria-hidden="true"
                />
              </button>
              {openIndex === index && (
                <div className="mt-2.5 pt-2.5" style={{ borderTop: '1px solid var(--gp-border)' }}>
                  <p className="text-sm leading-relaxed gp-text-muted">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 gp-soft-panel p-4">
          <p className="text-sm mb-3" style={{ color: 'var(--gp-ink)' }}>
            Still have questions? We're here to help!
          </p>
          <Link to="/contact" className="gp-btn-primary">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
